import { SHERLOCK_ACTIONS } from '../../../../../stateManagement/redux/actionPlans/types';

import { ORG_SERVICES } from 'blockscope/static/blockscopeServices';

/**
 * Parses a component ID string to extract tab name
 * @param {string} componentId - Format: 'profiler-{address}-{chain}-{tab}'
 * @returns {Object} Parsed component details
 */
const parseComponentId = (componentId) => {
  const parts = componentId.split('-');
  if (parts.length !== 4) {
    throw new Error(`Invalid component ID format: ${componentId}`);
  }
  return {
    type: parts[0],
    address: parts[1],
    chain: parts[2],
    tab: parts[3],
  };
};

/**
 * Creates an action plan to investigate a full wallet profile based on available tabs
 * @param {string} chain - The blockchain network (e.g., 'ethereum')
 * @param {string} address - The wallet address to investigate
 * @param {Object} state - Redux state
 * @returns {Object} Action plan configuration
 */
export const createFullWalletProfilePlan = (
  chain = 'ethereum',
  address,
  state
) => {
  const availableComponents = state.componentTracker.activeComponents;

  if (!availableComponents || availableComponents.length === 0) {
    console.warn('No components available in Redux state');
    return {
      name: ``,
      sherlockActions: [],
      prompt:
        "Conduct a comprehensive analysis of all wallet activities and characteristics. Review all available tabs to build a complete profile of the wallet's behavior, interactions, and patterns.",
    };
  }

  // Parse and filter components for this address and chain
  const relevantComponents = availableComponents.filter((componentId) => {
    try {
      const parsed = parseComponentId(componentId);
      return (
        parsed.type === 'profiler' &&
        parsed.address.toLowerCase() === address.toLowerCase() &&
        parsed.chain.toLowerCase() === chain.toLowerCase()
      );
    } catch (error) {
      console.warn(`Skipping invalid component ID: ${componentId}`, error);
      return false;
    }
  });

  const availableTabs = relevantComponents.map((componentId) => {
    const { tab } = parseComponentId(componentId);
    return tab;
  });

  const sherlockActions = availableTabs.map((tab) => ({
    type: SHERLOCK_ACTIONS.OPEN_PROFILER_TAB,
    params: {
      chain,
      address,
      tab,
    },
  }));

  return {
    name: `1-full-wallet-profile`,
    sherlockActions: [
      ...sherlockActions,
      {
        type: SHERLOCK_ACTIONS.GET_WALLET_DATA,
        params: { chain, address },
      },
    ],
    prompt:
      "Conduct a comprehensive analysis of all wallet activities and characteristics. Review all available tabs to build a complete profile of the wallet's behavior, interactions, and patterns.",
  };
};

/**
 * Helper function to check if tabs are available for a specific address and chain
 * @param {Object} state - Redux state
 * @param {string} address - Wallet address
 * @param {string} chain - Blockchain network
 * @returns {boolean}
 */
export const areTabsAvailable = (state, address, chain) => {
  if (!state.componentTracker.activeComponents) return false;

  return state.componentTracker.activeComponents.some((componentId) => {
    try {
      const parsed = parseComponentId(componentId);
      return (
        parsed.type === 'profiler' &&
        parsed.address.toLowerCase() === address.toLowerCase() &&
        parsed.chain.toLowerCase() === chain.toLowerCase()
      );
    } catch {
      return false;
    }
  });
};

/**
 * Creates an action plan focusing on KYC-relevant tabs
 * @param {string} chain - The blockchain network
 * @param {string} address - The wallet address to investigate
 * @param {Object} state - Redux state
 * @returns {Object} Action plan configuration
 */
export const createKYCInvestigationPlan = (
  chain = 'ethereum',
  address,
  state
) => {
  // KYC-relevant tabs in priority order
  const KYC_PRIORITY_TABS = ['LABELS', 'COUNTER_PARTIES', 'OVERVIEW'];

  const availableComponents = state.componentTracker.activeComponents;

  if (!availableComponents || availableComponents.length === 0) {
    console.warn('No components available in Redux state');
    return {
      name: ``,
      sherlockActions: [],
      prompt:
        'Investigate wallet for potential KYC identifiers and relationships. Focus on labels, counterparty interactions, and general wallet behavior to identify any patterns suggesting real-world identity.',
    };
  }

  // Parse and filter components for this address and chain
  const relevantComponents = availableComponents.filter((componentId) => {
    try {
      const parsed = parseComponentId(componentId);
      return (
        parsed.type === 'profiler' &&
        parsed.address.toLowerCase() === address.toLowerCase() &&
        parsed.chain.toLowerCase() === chain.toLowerCase()
      );
    } catch (error) {
      console.warn(`Skipping invalid component ID: ${componentId}`, error);
      return false;
    }
  });

  const availableTabs = relevantComponents.map((componentId) => {
    const { tab } = parseComponentId(componentId);
    return tab;
  });

  // Only include KYC priority tabs that exist in available tabs
  const sortedTabs = KYC_PRIORITY_TABS.filter((tab) =>
    availableTabs.includes(tab)
  );

  const sherlockActions = sortedTabs.map((tab) => ({
    type: SHERLOCK_ACTIONS.OPEN_PROFILER_TAB,
    params: {
      chain,
      address,
      tab,
    },
  }));

  return {
    name: `2-wallet-kyc`,
    sherlockActions: [
      ...sherlockActions,
      {
        type: SHERLOCK_ACTIONS.GET_WALLET_DATA,
        params: { chain, address },
      },
    ],
    prompt:
      'Investigate wallet for potential KYC identifiers and relationships. Focus on labels, counterparty interactions, and general wallet behavior to identify any patterns suggesting real-world identity.',
  };
};

export const createDecodeTraceCallPlan = (
  chain = 'ethereum',
  txnHash,
  state
) => {
  // KYC-relevant tabs in priority order
  const GO_TO_TABS = ['TRACE_CALL'];

  const actionPlan = GO_TO_TABS.map((tab) => ({
    type: SHERLOCK_ACTIONS.OPEN_DECODER_TAB,
    params: {
      chain,
      txnHash,
      tab,
    },
  }));

  return {
    name: `3-analyze-txn-trace-call`,
    sherlockActions: [
      ...actionPlan,
      {
        type: SHERLOCK_ACTIONS.GET_TXN_DATA,
        params: { chain, txnHash },
      },
    ],
    prompt:
      'Explain this trace call for this transaction. Break it down and explain it in detail.',
  };
};

export const createFullDecoderPlan = (chain = 'ethereum', txnHash, state) => {
  // KYC-relevant tabs in priority order
  const GO_TO_TABS = [
    'ANALYTICS',
    'ADDRESS_INTERACTION',
    'BALANCE_DIFFERENCES',
    'TOKEN_TRANSFERS',
    'LOGS',
    'FLOW_CHART',
    'TRACE_CALL',
    'SECURITY_RISK',
  ];

  const actionPlan = GO_TO_TABS.map((tab) => ({
    type: SHERLOCK_ACTIONS.OPEN_DECODER_TAB,
    params: {
      chain,
      txnHash,
      tab,
    },
  }));

  return {
    name: `4-full-txn-decode`,
    sherlockActions: [...actionPlan],
    prompt:
      'Conduct a comprehensive analysis of this decoded transaction data. Review all available tabs to build a complete understanding of what this transaction is, how it works, who was involved and any other details or patterns you can find',
  };
};

/**
 * Creates an action plan to investigate a full contract analysis based on available tabs
 * @param {string} chain - The blockchain network (e.g., 'ethereum')
 * @param {string} address - The contract address to investigate
 * @param {Object} state - Redux state
 * @returns {Object} Action plan configuration
 */
export const createFullContractAnalysisPlan = (
  chain = 'ethereum',
  address,
  state
) => {
  const availableComponents = state.componentTracker.activeComponents;

  if (!availableComponents || availableComponents.length === 0) {
    console.warn('No components available in Redux state');
    return {
      name: ``,
      sherlockActions: [],
      prompt:
        "Conduct a comprehensive analysis of all Contract details. Review all available tabs to build a complete profile of the Contract's ABI, Functions, Events, Constructor, and Errors.",
    };
  }

  // Parse and filter components for this address and chain
  const relevantComponents = availableComponents.filter((componentId) => {
    try {
      const parsed = parseComponentId(componentId);
      return (
        parsed.type === 'contractAnalysis' &&
        parsed.address.toLowerCase() === address.toLowerCase() &&
        parsed.chain.toLowerCase() === chain.toLowerCase()
      );
    } catch (error) {
      console.warn(`Skipping invalid component ID: ${componentId}`, error);
      return false;
    }
  });

  const availableTabs = relevantComponents.map((componentId) => {
    const { tab } = parseComponentId(componentId);
    return tab;
  });

  const sherlockActions = availableTabs.map((tab) => ({
    type: SHERLOCK_ACTIONS.OPEN_CONTRACT_ANALYSIS_TAB,
    params: {
      chain,
      address,
      tab,
    },
  }));

  // dispatch go to txn decoder, txn tab, search binance, take ss
  // collect data, add to master arrayy
  // dispatch 2nd

  return {
    name: `5-full-contract-analysis`,
    sherlockActions: [
      ...sherlockActions,
      {
        type: SHERLOCK_ACTIONS.GET_CONTRACT_ANALYSIS_DATA,
        params: { chain, address },
      },
    ],
    prompt:
      "Conduct a comprehensive analysis of all Contract details. Review all available tabs to build a complete profile of the Contract's ABI, Functions, Events, Constructor, and Errors.",
  };
};

export const AnalyzeCurrentPagePlan = () => {
  return {
    name: `7-analyze-current-page`,
    sherlockActions: [
      {
        type: SHERLOCK_ACTIONS.CURRENT_PAGE,
        params: {},
      },
    ],
    prompt: 'Explain The information on the current page in detail.',
  };
};

export const createNextTracerNodePlan = (nodes, tracerId) => {
  return {
    name: `6-tracer-next-node`,
    sherlockActions: [
      {
        type: SHERLOCK_ACTIONS.CAPTURE_TRACER_SCREENSHOT,
        params: { nodes },
      },
      {
        type: SHERLOCK_ACTIONS.GET_TRACER_DATA,
        params: { tracerId },
      },
    ],
    prompt:
      'Analyze the blockchain interactions and data provided. Identify suspicious patterns among the filtered nodes. Which nodes should be investigated next, give me up to 5 nodes?',
    service: ORG_SERVICES.TRACER_INVESTIGATION,
  };
};

